import React, { useState, useEffect } from 'react';
import { doc, getDoc, deleteDoc, updateDoc, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import { hasActiveCard, confirmCardRequired } from '../utils/cardGate';
import { getFeatures } from '../utils/features';
import { invalidateReceivables } from '../utils/receivables';
import { invalidateSummaryCache } from './statement/SummaryCards';
import { TEST_ISSUE_LABEL, isTestIssuedInvoice, isOneClickRecordOnly } from './TaxInvoiceList';
import FlowBar from './common/FlowBar';
import { ArrowLeft, Printer, Trash2, Zap, CheckCircle, AlertTriangle } from 'lucide-react';

// 용어 통일 (2026-10-24): 작성 중 / 발행됨 / 수정 요청 중 / 확인 완료
const STATUS_MAP = {
    draft: { label: '작성 중', bg: '#EEF2FF', color: '#4338CA' },
    issued: { label: '발행됨', bg: '#FFF7E8', color: '#B45309' },
    sent_to_nts: { label: '국세청 전송', bg: '#DBEAFE', color: '#1D4ED8' },
    nts_confirmed: { label: '국세청 확인', bg: '#EDE9FE', color: '#6D28D9' },
    cancelled: { label: '취소', bg: '#FEE2E2', color: '#991B1B' },
};

const fmt = (v) => (v || 0).toLocaleString();

const fmtDate = (val) => {
    if (!val) return '';
    try {
        if (val.toDate) return fmtShort(val.toDate());
        if (val.seconds) return fmtShort(new Date(val.seconds * 1000));
        if (typeof val === 'string' && val.length >= 10) return fmtShort(new Date(val));
    } catch {}
    return '';
};

const fmtShort = (d) => `${d.getMonth() + 1}/${d.getDate()}`;

const fmtFull = (val) => {
    if (!val) return '-';
    try {
        let d;
        if (val.toDate) d = val.toDate();
        else if (val.seconds) d = new Date(val.seconds * 1000);
        else d = new Date(val);
        return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
    } catch { return '-'; }
};

const PRINT_STYLES = `
    @media print {
        html, body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        @page {
            size: A4 portrait;
            margin: 0;
        }
        .no-print { display: none !important; }
        .print-wrapper {
            padding: 0 !important;
            max-width: none !important;
        }
        .invoice-page {
            width: 210mm !important;
            min-height: 297mm !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 12mm 15mm 10mm 15mm !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
        }
        .invoice-page table {
            page-break-inside: avoid;
        }
        .items-table-wrap {
            flex: 1 !important;
        }
        .items-table-wrap table {
            height: 100%;
        }
    }
`;

// 표준 셀 스타일
const bd = '1px solid #555';
const S = {
    cell: { border: bd, padding: '3px 5px', fontSize: '10px', verticalAlign: 'middle' },
    hCell: { border: bd, padding: '3px 5px', fontSize: '10px', fontWeight: '600', background: '#F0F4F8', textAlign: 'center', verticalAlign: 'middle' },
    lCell: { border: bd, padding: '3px 5px', fontSize: '10px', fontWeight: '600', background: '#F0F4F8', textAlign: 'center', verticalAlign: 'middle', width: '58px' },
};

// readOnly: 결제 전 기공소(제한 모드) — 보기·인쇄·국세청 상태 조회만, 발행·전자발행·삭제·확인 숨김 (계획_가입승인결제흐름 W6, 법무 R4)
function TaxInvoiceDetail({ invoiceId, user, onBack, readOnly = false }) {
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [issuing, setIssuing] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);   // ⋯ 보조 메뉴
    const targetId = user?.companyId || user?.uid;
    // 계산서 발행·삭제 뒤 원장(받을 돈)·요약 카드 캐시 지우기 (명세 R-3) — 계산서 발행자 = 기공소 회사 id
    const invalidateLedgerCaches = () => {
        const labId = invoice?.createdBy || targetId;
        invalidateReceivables(labId);
        invalidateSummaryCache(labId);
    };

    useEffect(() => {
        fetchInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceId]);

    const fetchInvoice = async () => {
        setLoading(true);
        try {
            const snap = await getDoc(doc(db, 'taxInvoices', invoiceId));
            if (snap.exists()) setInvoice({ id: snap.id, ...snap.data() });
        } catch (e) {
            console.error('세금계산서 로드 실패:', e);
        }
        setLoading(false);
    };

    const handleElectronicIssue = async () => {
        // 팝빌 운영키 전환 전까지 전자발행 잠금 (2026-07-24 회의 결정 — 테스트 모드 실발행 사고 방지)
        // 오픈 = config/features.popbillLive=true (배포 불필요)
        try {
            const f = await getFeatures();
            if (f?.popbillLive !== true) {
                alert('국세청 전자발행은 준비 중입니다. (일반 발행·인쇄는 지금도 가능합니다)\n전자발행이 열리면 알려드리겠습니다.');
                return;
            }
        } catch (_) { alert('국세청 전자발행은 준비 중입니다.'); return; }
        // 전자발행은 유료 기능 — 결제 카드 등록 필수
        if (!(await hasActiveCard(user))) { confirmCardRequired(); return; }
        // 미가입 치과 전자발행 전 추가 확인 (2026-07-30 대표 결정) — 국세청에 실제로 남는 행위이고,
        // 상호가 CAD 파일에서 자동으로 읽힌 이름일 수 있어 발행 전에 사람이 한 번 더 대조하게 한다.
        const buyer = invoice?.buyerInfo || {};
        const nonMember = !buyer.userId
            || String(buyer.userId).startsWith('agent_')
            || String(buyer.userId).startsWith('email_');
        if (nonMember) {
            const ok = window.confirm(
                `덴클에 가입하지 않은 거래처입니다.\n\n` +
                `상호: ${buyer.name || '(없음)'}\n` +
                `사업자등록번호: ${buyer.bizNumber || '(없음)'}\n\n` +
                `전자발행하면 국세청에 그대로 남고, 상대 치과에도 국세청을 통해 전달됩니다.\n` +
                `상호·사업자등록번호가 맞는지 다시 확인해주세요.\n\n` +
                `계속할까요?`
            );
            if (!ok) return;
        }
        if (!window.confirm('이 세금계산서를 국세청에 전자발행하시겠습니까?\n\n발행 후에는 취소가 어려울 수 있습니다.')) return;
        setIssuing(true);
        try {
            const functions = getFunctions(undefined, 'asia-northeast3');
            const issueElectronic = httpsCallable(functions, 'issueElectronicTaxInvoice');
            const res = await issueElectronic({ invoiceId });
            alert(res.data?.isTest
                ? '전자발행이 완료되었습니다. (팝빌 테스트 모드 — 실제 국세청 전송 아님)'
                : '전자발행이 완료되었습니다.');
            invalidateLedgerCaches();
            await fetchInvoice(); // 최신 상태 반영
        } catch (e) {
            console.error('전자발행 실패:', e);
            alert('전자발행 실패: ' + (e.message || '오류가 발생했습니다.'));
            await fetchInvoice(); // 오류 상태 배너 반영
        }
        setIssuing(false);
    };

    // 전자발행 건 상태 새로고침 (국세청 전송 결과 확인)
    const [checking, setChecking] = useState(false);
    const handleCheckStatus = async () => {
        setChecking(true);
        try {
            const functions = getFunctions(undefined, 'asia-northeast3');
            const check = httpsCallable(functions, 'checkPopbillStatus');
            const res = await check({ invoiceId });
            const info = res.data?.data || {};
            alert(`팝빌 상태: ${info.stateCode || '-'}\n국세청 결과: ${info.ntsresult || info.ntsResult || '대기'}`);
            await fetchInvoice();
        } catch (e) {
            alert('상태 조회 실패: ' + (e.message || '오류'));
        }
        setChecking(false);
    };

    // draft → 발행 전환 (기존엔 draft를 발행할 방법이 없어 dead-end)
    const [publishing, setPublishing] = useState(false);
    const handlePublishDraft = async () => {
        // 비회원 치과도 발행 허용 (2026-07-23) — 사업자번호만 있으면 가능, 회원 아니면 알림만 생략
        if (!invoice.buyerInfo?.userId && !invoice.buyerInfo?.bizNumber) {
            alert('발행하려면 공급받는자의 사업자등록번호가 필요합니다.\n(계산서를 열어 사업자번호를 입력 후 다시 시도해주세요)');
            return;
        }
        // 발행은 유료 기능 — 결제 카드 등록 필수
        if (!(await hasActiveCard(user))) { confirmCardRequired(); return; }
        const isMemberBuyer = !!invoice.buyerInfo?.userId && !String(invoice.buyerInfo.userId).startsWith('agent_') && !String(invoice.buyerInfo.userId).startsWith('email_');
        if (!window.confirm(`이 ${invoice.isTaxExempt ? '계산서' : '세금계산서'}를 발행하시겠습니까?${isMemberBuyer ? '\n거래처에 알림이 발송됩니다.' : '\n(덴클 미가입 치과 — 알림 없이 발행 기록만 생성됩니다)'}`)) return;
        setPublishing(true);
        try {
            await updateDoc(doc(db, 'taxInvoices', invoiceId), { status: 'issued', issuedAt: new Date() });
            const linkedStmtId = invoice.linkedStatementId || invoice.statementId;   // 앱은 statementId 필드로 저장 (호환)
            if (linkedStmtId) {
                try {
                    await updateDoc(doc(db, 'transactionStatements', linkedStmtId), { taxInvoiceStatus: 'issued' });
                } catch (e) { console.warn('명세서 상태 갱신 실패:', e); }
            }
            try {
                if (isMemberBuyer) await createNotification({
                    recipientId: invoice.buyerInfo.userId,
                    type: 'tax_invoice_received',
                    title: invoice.isTaxExempt ? '계산서 수신' : '세금계산서 수신',
                    message: `${invoice.supplierInfo?.name || '거래처'}에서 ${invoice.isTaxExempt ? '계산서' : '세금계산서'}를 발행했습니다. (${invoice.invoiceNumber} · ${(invoice.totalAmount || 0).toLocaleString()}원)\n내용을 확인해주세요.`,
                    link: '/tax-invoices',
                });
            } catch (e) { console.error('알림 실패:', e); }
            invalidateLedgerCaches();   // 발행 계산서 세액이 원장에 들어감 (R-3)
            await fetchInvoice();
            alert('발행되었습니다.');
        } catch (e) {
            console.error('계산서 발행 실패:', invoiceId, e?.code || e?.message);
            alert(`발행에 실패했습니다. (오류: ${e?.code || e?.message || '알 수 없음'})`);
        }
        setPublishing(false);
    };

    // ── 공급받는자(치과) 확인/수정 요청 — 수신 입장의 검수 흐름 ──
    const [processing, setProcessing] = useState(false);

    const handleBuyerConfirm = async () => {
        if (!window.confirm('이 계산서 내용을 확인 완료 처리하시겠습니까?\n확인 기록이 발행처에 전달됩니다.')) return;
        setProcessing(true);
        try {
            await updateDoc(doc(db, 'taxInvoices', invoiceId), {
                buyerStatus: 'confirmed',
                buyerConfirmedAt: new Date(),
                buyerConfirmedBy: user?.uid || targetId,
            });
            try {
                await createNotification({
                    recipientId: invoice.createdBy,
                    type: 'tax_invoice_confirmed',
                    title: '계산서 확인 완료',
                    message: `${invoice.buyerInfo?.name || '거래처'}에서 ${invoice.isTaxExempt ? '계산서' : '세금계산서'}(${invoice.invoiceNumber})를 확인했습니다.`,
                    link: '/tax-invoices',
                });
            } catch (e) { console.error('확인 알림 실패:', e); }
            await fetchInvoice();
            alert('확인 완료 처리되었습니다.');
        } catch (e) {
            console.error('확인 처리 실패:', e);
            alert('확인 처리에 실패했습니다.');
        }
        setProcessing(false);
    };

    const handleBuyerRevisionRequest = async () => {
        const reason = window.prompt('수정이 필요한 내용을 입력해주세요.\n(예: 3월 15일 홍길동 크라운 단가가 명세서와 다릅니다)');
        if (reason == null) return;
        if (!reason.trim()) { alert('수정 요청 사유를 입력해주세요.'); return; }
        setProcessing(true);
        try {
            await updateDoc(doc(db, 'taxInvoices', invoiceId), {
                buyerStatus: 'revision_requested',
                buyerRevisionReason: reason.trim(),
                buyerRevisionAt: new Date(),
                buyerConfirmedBy: user?.uid || targetId,
            });
            try {
                await createNotification({
                    recipientId: invoice.createdBy,
                    type: 'tax_invoice_revision_requested',
                    title: '계산서 수정 요청',
                    message: `${invoice.buyerInfo?.name || '거래처'}에서 ${invoice.isTaxExempt ? '계산서' : '세금계산서'}(${invoice.invoiceNumber}) 수정을 요청했습니다.\n사유: ${reason.trim()}`,
                    link: '/tax-invoices',
                });
            } catch (e) { console.error('수정요청 알림 실패:', e); }
            await fetchInvoice();
            alert('수정 요청이 전송되었습니다.');
        } catch (e) {
            console.error('수정 요청 실패:', e);
            alert('수정 요청에 실패했습니다.');
        }
        setProcessing(false);
    };

    const handleDelete = async () => {
        if (!window.confirm('이 전자세금계산서를 삭제하시겠습니까?')) return;
        setDeleting(true);
        try {
            // 삭제 먼저 — 삭제가 실패했는데 명세서 잠금만 풀리는 역순 사고 방지
            await deleteDoc(doc(db, 'taxInvoices', invoiceId));
            invalidateLedgerCaches();   // 삭제로 원장 세액이 바뀜 (R-3)
            // 연결된 거래명세서 잠금 해제 — 안 풀면 그 명세서로 재발행 영구 불가
            const linkedStmtId2 = invoice?.linkedStatementId || invoice?.statementId;   // 앱 생성분(statementId) 호환
            if (linkedStmtId2) {
                try {
                    // 면세 검수 P1-2 (2026-08-25): 명세서가 지금 "이 계산서"를 가리킬 때만 해제 —
                    // 고아 draft를 정리 삭제할 때 살아있는 발행본의 연결을 끊어 같은 명세서로 발행본 2장이 되는 경로 차단
                    const stmtSnap = await getDoc(doc(db, 'transactionStatements', linkedStmtId2));
                    if (stmtSnap.exists() && stmtSnap.data().taxInvoiceId === invoiceId) {
                        await updateDoc(doc(db, 'transactionStatements', linkedStmtId2), {
                            taxInvoiceId: deleteField(),
                            taxInvoiceStatus: deleteField(),
                        });
                    }
                } catch (e) {
                    console.warn('명세서 잠금 해제 실패:', e);
                    // 조용한 실패 금지 (면세 검수 P2-6): 해제 실패 = 그 명세서로 재발행이 계속 막히는 상태 — 사용자에게 고지
                    alert('계산서는 삭제됐지만 연결된 거래명세서의 잠금 해제에 실패했습니다.\n그 명세서로 재발행이 안 되면 고객센터(010-2972-3733)로 연락해주세요.');
                }
            }
            onBack && onBack();
        } catch (e) {
            console.error('계산서 삭제 실패:', invoiceId, e?.code || e?.message);
            alert(`삭제에 실패했습니다. (오류: ${e?.code || e?.message || '알 수 없음'})`);
        }
        setDeleting(false);
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#94A3B8' }}>로딩 중...</div>;
    if (!invoice) return <div style={{ textAlign: 'center', padding: '60px', color: '#94A3B8' }}>전자세금계산서를 찾을 수 없습니다.</div>;

    // 품목별 세액 — 합계(invoice.taxAmount)와 일치하도록 마지막 품목에서 반올림 오차 보정
    const printItems = invoice.items || [];
    const isExemptDoc = invoice.isTaxExempt === true;
    const itemTaxes = (() => {
        if (isExemptDoc) return printItems.map(() => 0);
        const taxes = printItems.map(it => Math.round((Number(it.amount) || 0) * 0.1));
        const totalTax = Number(invoice.taxAmount) || 0;
        const diff = totalTax - taxes.reduce((s, t) => s + t, 0);
        if (taxes.length > 0 && diff !== 0) taxes[taxes.length - 1] += diff;
        return taxes;
    })();

    const isDraft = invoice.status === 'draft';
    const isCreator = invoice.createdBy === targetId;
    // 공급받는자(치과) — 수신 입장: 확인/수정 요청 가능
    const isBuyer = !isCreator && (invoice.buyerInfo?.userId === targetId || invoice.buyerInfo?.userId === user?.uid);
    const canBuyerReview = !readOnly && isBuyer && invoice.status === 'issued' && invoice.buyerStatus !== 'confirmed';
    // 수정 요청을 받은 발행본은 발행자가 삭제 후 재발행 가능 (전자발행된 것은 국세청 취소 필요 — 제외)
    const canDelete = !readOnly && isCreator && !invoice.isElectronic && (isDraft || invoice.buyerStatus === 'revision_requested');
    const canElectronicIssue = !readOnly && invoice.status === 'issued' && isCreator && !invoice.isElectronic;
    const statusCfg = STATUS_MAP[invoice.status] || { label: invoice.status, bg: '#F1F5F9', color: '#475569' };
    const isTaxExempt = invoice.isTaxExempt || false;
    const docTitle = isTaxExempt ? '전 자 계 산 서' : '전 자 세 금 계 산 서';
    const sup = invoice.supplierInfo || {};
    const buy = invoice.buyerInfo || {};
    const items = invoice.items || [];
    const colCount = isTaxExempt ? 7 : 8;

    // A4에 맞게 빈 행 계산 — 상단 영역이 약 110mm, 하단 40mm → 품목 영역 약 147mm, 행 높이 약 6.5mm
    const maxRows = Math.max(10, Math.floor(147 / 6.5));
    const emptyRows = Math.max(0, maxRows - items.length - 1); // -1 for 합계 row

    // 진행 스테퍼 상태 계산 (작성 중 → 발행됨 → 치과 확인)
    const flow = (() => {
        const docName = isTaxExempt ? '계산서' : '세금계산서';
        if (isDraft) {
            return isCreator
                ? { current: 0, tone: 'pri', message: `내용을 확인하고 발행하세요.`, small: '발행 전에는 거래처에 보이지 않습니다' }
                : { current: 0, tone: 'wait', message: `${docName}가 작성 중입니다.`, small: '발행되면 알림으로 알려드립니다' };
        }
        if (invoice.buyerStatus === 'revision_requested') {
            return isCreator
                ? { current: 1, tone: 'warn', message: '수신처에서 수정을 요청했습니다.', small: '아래 요청 사유를 확인하고, 삭제 후 재발행해주세요 (연결된 명세서 잠금은 자동 해제됩니다)' }
                : { current: 1, tone: 'wait', message: '수정 요청을 보냈습니다. 발행처가 수정하는 중입니다.', small: '재발행되면 알림으로 알려드립니다' };
        }
        if (invoice.buyerStatus === 'confirmed') {
            return { current: 3, tone: 'done', message: `수신처 확인까지 완료된 ${docName}입니다.`, small: invoice.isElectronic ? '' : '필요 시 [전자발행]으로 국세청에 전송할 수 있습니다' };
        }
        // issued, 치과 확인 대기
        return isBuyer
            ? { current: 2, tone: 'pri', message: '내역을 확인해주세요.', small: '문제가 없으면 [확인 완료], 다른 부분이 있으면 [수정 요청]을 눌러주세요' }
            : { current: 2, tone: 'wait', message: '수신처(치과)가 내용을 확인하는 중입니다.', small: '확인 또는 수정 요청이 오면 알림으로 알려드립니다' };
    })();

    return (
        <div className="print-wrapper" style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            <style>{PRINT_STYLES}</style>

            <FlowBar
                steps={['작성 중', '발행됨', '치과 확인']}
                current={flow.current}
                tone={flow.tone}
                message={flow.message}
                small={flow.small}
            />

            {/* 액션 바 */}
            <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button onClick={onBack} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '8px', cursor: 'pointer' }}>
                    <ArrowLeft size={18} />
                </button>
                <span style={{ flex: 1, fontSize: '18px', fontWeight: '700', color: '#1E293B' }}>
                    {isTaxExempt ? '전자계산서' : '전자세금계산서'} 상세
                </span>
                {invoice.isElectronic && (
                    <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#DBEAFE', color: '#1D4ED8' }}>
                        전자
                    </span>
                )}
                {/* 명세서 원클릭 발행분 (서버 생성, 2026-10-24 2단계 ⑤) */}
                {invoice.source === 'oneclick' && (
                    <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: '#ECFDF5', color: '#047857' }}>
                        {isOneClickRecordOnly(invoice) ? '명세서 원클릭 · 덴클 발행 기록' : '명세서 원클릭'}
                    </span>
                )}
                {isTestIssuedInvoice(invoice) && (
                    <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#FFFBEB', color: '#92400E' }}>
                        {TEST_ISSUE_LABEL}
                    </span>
                )}
                <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: statusCfg.bg, color: statusCfg.color }}>
                    {statusCfg.label}
                </span>
                {/* 공급받는자 확인 상태 배지 (양쪽 모두에게 표시) */}
                {invoice.buyerStatus === 'confirmed' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#D1FAE5', color: '#065F46' }}>
                        <CheckCircle size={13} /> 수신처 확인완료
                    </span>
                )}
                {invoice.buyerStatus === 'revision_requested' && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#FEF2F2', color: '#DC2626' }}>
                        <AlertTriangle size={13} /> 수정 요청 중
                    </span>
                )}
                {/* 공급받는자(치과) 전용: 확인 / 수정 요청 */}
                {canBuyerReview && (
                    <>
                        <button onClick={handleBuyerConfirm} disabled={processing} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: processing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', opacity: processing ? 0.7 : 1 }}>
                            <CheckCircle size={15} /> 확인 완료
                        </button>
                        <button onClick={handleBuyerRevisionRequest} disabled={processing} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA', borderRadius: '8px', cursor: processing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
                            <AlertTriangle size={15} /> 수정 요청
                        </button>
                    </>
                )}
                {isDraft && isCreator && !readOnly && (
                    <button onClick={handlePublishDraft} disabled={publishing} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: publishing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', opacity: publishing ? 0.7 : 1 }}>
                        <Zap size={15} /> {publishing ? '발행 중...' : '발행하기'}
                    </button>
                )}
                {canElectronicIssue && (
                    <button onClick={handleElectronicIssue} disabled={issuing} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#4F46E5', color: 'white', border: 'none', borderRadius: '8px', cursor: issuing ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px', opacity: issuing ? 0.7 : 1 }}>
                        <Zap size={15} /> {issuing ? '발행 중...' : '전자발행'}
                    </button>
                )}
                {/* 보조 동작은 ⋯ 메뉴로 (인쇄 · 국세청 상태 · 삭제) */}
                <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowMoreMenu(v => !v)} title="인쇄 · 삭제 등"
                        style={{ padding: '8px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: '800', color: '#475569' }}>
                        ⋯
                    </button>
                    {showMoreMenu && (
                        <div style={{ position: 'absolute', top: '110%', right: 0, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 160, overflow: 'hidden' }}>
                            <button onClick={() => { setShowMoreMenu(false); window.print(); }}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#334155', textAlign: 'left' }}>
                                <Printer size={15} /> 인쇄
                            </button>
                            {invoice.isElectronic && (
                                <button onClick={() => { setShowMoreMenu(false); handleCheckStatus(); }} disabled={checking}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#334155', textAlign: 'left' }}>
                                    <Zap size={15} /> {checking ? '조회 중...' : '국세청 상태 확인'}
                                </button>
                            )}
                            {canDelete && (
                                <button onClick={() => { setShowMoreMenu(false); handleDelete(); }} disabled={deleting}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#DC2626', textAlign: 'left' }}>
                                    <Trash2 size={15} /> 삭제
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 전자발행 오류 배너 */}
            {invoice.electronicStatus === 'error' && invoice.electronicError && (
                <div className="no-print" style={{ maxWidth: '780px', margin: '0 auto 16px', padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', fontSize: '13px', color: '#991B1B' }}>
                    <b>전자발행 실패</b> — {invoice.electronicError}
                    {isCreator && <div style={{ marginTop: 6, fontSize: 12 }}>내용 확인 후 [전자발행]을 다시 시도할 수 있습니다. (이중 발행은 자동 차단됩니다)</div>}
                </div>
            )}
            {isTestIssuedInvoice(invoice) && (
                <div className="no-print" style={{ maxWidth: '780px', margin: '0 auto 16px', padding: '10px 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', fontSize: '12.5px', color: '#92400E' }}>
                    ⚠ <b>{TEST_ISSUE_LABEL}</b> — 팝빌 테스트 모드로 발행된 건이라 실제 국세청에 전송되지 않았습니다.
                </div>
            )}
            {/* 원클릭 '덴클 발행 기록' — 전자발행 전 (팝빌 잠금 중 생성분) */}
            {isOneClickRecordOnly(invoice) && invoice.status !== 'draft' && (
                <div className="no-print" style={{ maxWidth: '780px', margin: '0 auto 16px', padding: '10px 16px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', fontSize: '12.5px', color: '#475569' }}>
                    {'덴클 안 발행 기록입니다 — 국세청 전송은 아직 열리지 않았습니다.'}
                </div>
            )}

            {/* 수정 요청 사유 배너 */}
            {invoice.buyerStatus === 'revision_requested' && invoice.buyerRevisionReason && (
                <div className="no-print" style={{ maxWidth: '780px', margin: '0 auto 16px', padding: '12px 16px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', fontSize: '13px', color: '#9A3412' }}>
                    <b>수신처 수정 요청</b> — {invoice.buyerRevisionReason}
                    {isCreator && (
                        <div style={{ marginTop: 6, fontSize: 12, color: '#C2410C' }}>
                            임시저장 상태가 아니면 이 계산서를 삭제 후 재발행해주세요. (연결된 명세서 잠금은 삭제 시 자동 해제됩니다)
                        </div>
                    )}
                </div>
            )}

            {/* ====== A4 인쇄 영역 ====== */}
            <div className="invoice-page" style={{
                background: 'white',
                border: '2px solid #555',
                borderRadius: '4px',
                padding: '24px 28px 16px',
                fontFamily: "'Noto Sans KR', 'Malgun Gothic', sans-serif",
                lineHeight: 1.3,
                maxWidth: '780px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
            }}>

                {/* 제목 */}
                <div style={{ textAlign: 'center', paddingBottom: '8px', borderBottom: '2px double #555', marginBottom: '8px' }}>
                    <div style={{ fontSize: '22px', fontWeight: '900', letterSpacing: '10px', color: isTaxExempt ? '#1D4ED8' : '#1a1a1a' }}>
                        {docTitle}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', marginTop: '2px' }}>( 공급자 보관용 )</div>
                </div>

                {/* 일련번호 + 작성일 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '6px', padding: '0 2px' }}>
                    <span><strong>일련번호:</strong> {invoice.invoiceNumber}</span>
                    <span><strong>작성일:</strong> {fmtFull(invoice.issueDate)}</span>
                </div>

                {/* 공급자 / 공급받는자 2단 테이블 */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
                    <tbody>
                        <tr>
                            <td style={{ width: '50%', verticalAlign: 'top', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td rowSpan="4" style={{ ...S.hCell, width: '22px', writingMode: 'vertical-rl', letterSpacing: '4px', background: '#E8F0FE', fontWeight: '800', fontSize: '12px' }}>공급자</td>
                                            <td style={S.lCell}>등록번호</td>
                                            <td colSpan="3" style={{ ...S.cell, fontWeight: '700', letterSpacing: '1px' }}>{sup.bizNumber || sup.businessNumber || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>상호</td>
                                            <td style={{ ...S.cell, fontWeight: '600' }}>{sup.name || sup.businessName || '-'}</td>
                                            <td style={{ ...S.lCell, width: '40px' }}>대표</td>
                                            <td style={S.cell}>{sup.ceoName || sup.representative || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>주소</td>
                                            <td colSpan="3" style={{ ...S.cell, fontSize: '9px' }}>{sup.address || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>업태</td>
                                            <td style={S.cell}>{sup.bizType || '-'}</td>
                                            <td style={{ ...S.lCell, width: '40px' }}>종목</td>
                                            <td style={S.cell}>{sup.bizItem || sup.bizCategory || '-'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ width: '50%', verticalAlign: 'top', padding: 0 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td rowSpan="4" style={{ ...S.hCell, width: '22px', writingMode: 'vertical-rl', letterSpacing: '2px', background: '#FEF3C7', fontWeight: '800', fontSize: '11px' }}>공급받는자</td>
                                            <td style={S.lCell}>등록번호</td>
                                            <td colSpan="3" style={{ ...S.cell, fontWeight: '700', letterSpacing: '1px' }}>{buy.bizNumber || buy.businessNumber || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>상호</td>
                                            <td style={{ ...S.cell, fontWeight: '600' }}>{buy.name || buy.businessName || '-'}</td>
                                            <td style={{ ...S.lCell, width: '40px' }}>대표</td>
                                            <td style={S.cell}>{buy.ceoName || buy.representative || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>주소</td>
                                            <td colSpan="3" style={{ ...S.cell, fontSize: '9px' }}>{buy.address || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td style={S.lCell}>업태</td>
                                            <td style={S.cell}>{buy.bizType || '-'}</td>
                                            <td style={{ ...S.lCell, width: '40px' }}>종목</td>
                                            <td style={S.cell}>{buy.bizItem || buy.bizCategory || '-'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 합계금액 바 */}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ ...S.hCell, width: '70px', fontSize: '11px', fontWeight: '800' }}>합계금액</td>
                            <td style={{ ...S.cell, textAlign: 'center', fontWeight: '800', fontSize: '14px', letterSpacing: '1px' }}>
                                ￦ {fmt(invoice.totalAmount)}
                            </td>
                            <td style={{ ...S.hCell, width: '60px' }}>공급가액</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '100px', fontWeight: '600' }}>{fmt(invoice.supplyAmount)}</td>
                            <td style={{ ...S.hCell, width: '45px' }}>{isTaxExempt ? '' : '세액'}</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '100px', fontWeight: '600' }}>
                                {isTaxExempt ? <span style={{ color: '#3B82F6', fontSize: '9px' }}>면세</span> : fmt(invoice.taxAmount)}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* 품목 테이블 — flex: 1 로 남은 공간 채움 */}
                <div className="items-table-wrap" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', flex: 1 }}>
                        <thead>
                            <tr>
                                <th style={{ ...S.hCell, width: '48px' }}>입고일</th>
                                <th style={{ ...S.hCell, width: '48px' }}>납품일</th>
                                <th style={S.hCell}>품 목</th>
                                <th style={{ ...S.hCell, width: '38px' }}>수량</th>
                                <th style={{ ...S.hCell, width: '65px' }}>단 가</th>
                                <th style={{ ...S.hCell, width: '75px' }}>공급가액</th>
                                {!isTaxExempt && <th style={{ ...S.hCell, width: '62px' }}>세 액</th>}
                                <th style={{ ...S.hCell, width: '48px' }}>비고</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const supply = item.amount || 0;
                                const tax = itemTaxes[idx] ?? 0;
                                return (
                                    <tr key={idx}>
                                        <td style={{ ...S.cell, textAlign: 'center', fontSize: '9px' }}>{fmtDate(item.receiptDate) || fmtDate(item.date)}</td>
                                        <td style={{ ...S.cell, textAlign: 'center', fontSize: '9px' }}>{fmtDate(item.deliveryDate)}</td>
                                        <td style={{ ...S.cell, fontSize: '9px' }}>{item.description}</td>
                                        <td style={{ ...S.cell, textAlign: 'right' }}>{item.quantity}</td>
                                        <td style={{ ...S.cell, textAlign: 'right' }}>{fmt(item.unitPrice)}</td>
                                        <td style={{ ...S.cell, textAlign: 'right' }}>{fmt(supply)}</td>
                                        {!isTaxExempt && <td style={{ ...S.cell, textAlign: 'right' }}>{fmt(tax)}</td>}
                                        <td style={S.cell}></td>
                                    </tr>
                                );
                            })}
                            {Array.from({ length: emptyRows }).map((_, idx) => (
                                <tr key={`e-${idx}`}>
                                    {Array.from({ length: colCount }).map((__, ci) => (
                                        <td key={ci} style={S.cell}>&nbsp;</td>
                                    ))}
                                </tr>
                            ))}
                            {/* 합계 행 */}
                            <tr style={{ background: '#F0F4F8' }}>
                                <td colSpan="2" style={{ ...S.hCell, fontWeight: '800', fontSize: '11px' }}>합 계</td>
                                <td style={S.cell}></td>
                                <td style={{ ...S.cell, textAlign: 'right', fontWeight: '700' }}>
                                    {items.reduce((s, i) => s + (Number(i.quantity) || 0), 0)}
                                </td>
                                <td style={S.cell}></td>
                                <td style={{ ...S.cell, textAlign: 'right', fontWeight: '700' }}>{fmt(invoice.supplyAmount)}</td>
                                {!isTaxExempt && <td style={{ ...S.cell, textAlign: 'right', fontWeight: '700' }}>{fmt(invoice.taxAmount)}</td>}
                                <td style={S.cell}></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* 현금/수표/어음/외상미수금 */}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ ...S.hCell, width: '12.5%' }}>현금</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '12.5%' }}></td>
                            <td style={{ ...S.hCell, width: '12.5%' }}>수표</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '12.5%' }}></td>
                            <td style={{ ...S.hCell, width: '12.5%' }}>어음</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '12.5%' }}></td>
                            <td style={{ ...S.hCell, width: '12.5%' }}>외상미수금</td>
                            <td style={{ ...S.cell, textAlign: 'right', width: '12.5%', fontWeight: '700' }}>{fmt(invoice.totalAmount)}</td>
                        </tr>
                    </tbody>
                </table>

                {/* 비고 */}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr>
                            <td style={{ ...S.hCell, width: '58px' }}>비고</td>
                            <td style={{ ...S.cell, minHeight: '20px' }}>{invoice.remarks || ''}</td>
                        </tr>
                    </tbody>
                </table>

                {/* 하단 안내문 */}
                <div style={{ padding: '6px 0', fontSize: '9px', color: '#888', textAlign: 'center', borderTop: '1px solid #555', marginTop: '2px' }}>
                    위 금액을 {isTaxExempt ? '청구' : '청구'}함.&emsp;이 {isTaxExempt ? '전자계산서' : '전자세금계산서'}는 부가가치세법에 의하여 교부하는 것입니다.
                </div>
            </div>
        </div>
    );
}

export default TaxInvoiceDetail;
